import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getSeoFor, getSettings, buildJsonLd } from "@/lib/publicSettings";

// Dependency-free <head> manager. On each route change, loads merged
// global+page SEO and sets title + meta + canonical + JSON-LD.
//
// Standard meta tags (description/robots/og/twitter) are UPSERTED in place so
// we update the static index.html defaults rather than duplicating them.
// Canonical + JSON-LD are dynamic, so they're tagged data-managed-seo and
// fully replaced each route.
const ATTR = "data-managed-seo";

function upsertMeta(attrName, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attrName}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

export default function useSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;
    (async () => {
      const [seo, settings] = await Promise.all([getSeoFor(pathname), getSettings()]);
      if (!alive) return;

      // Replace dynamic managed nodes (canonical + JSON-LD).
      document.querySelectorAll(`[${ATTR}]`).forEach((n) => n.remove());

      if (seo.title) document.title = seo.title;
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const base = (seo.canonicalBase || origin).replace(/\/$/, "");
      const url = base ? base + (pathname === "/" ? "" : pathname) : undefined;

      upsertMeta("name", "description", seo.description);
      upsertMeta("name", "keywords", seo.keywords);
      upsertMeta("name", "robots", seo.robots);
      upsertMeta("property", "og:title", seo.title);
      upsertMeta("property", "og:description", seo.description);
      upsertMeta("property", "og:type", seo.ogType || "website");
      upsertMeta("property", "og:image", seo.ogImage);
      if (url) upsertMeta("property", "og:url", url);
      upsertMeta("name", "twitter:card", seo.twitterCard || "summary_large_image");
      upsertMeta("name", "twitter:title", seo.title);
      upsertMeta("name", "twitter:image", seo.ogImage);

      if (url) {
        const link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        link.setAttribute("href", url);
        link.setAttribute(ATTR, "1");
        document.head.appendChild(link);
      }

      buildJsonLd(seo, settings, url || base).forEach((block) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute(ATTR, "1");
        s.textContent = JSON.stringify(block);
        document.head.appendChild(s);
      });
    })();

    return () => {
      alive = false;
    };
  }, [pathname]);
}
